/**
 * Utility to parse EC Listing import Excel files (.xlsx/.xls)
 * Parses only the data from Excel - everything else is handled by existing functions.
 */

import Logger, { LOG_LEVELS } from './logger';
import { mapECTypeFromTemplate } from './ecListingImportConfig';

const log = new Logger('EC_LISTING_EXCEL_PARSER', LOG_LEVELS.DEBUG);

/**
 * Expected column headers in the template
 */
const EXPECTED_HEADERS = {
  VAT_NUMBER: 'VATNumber',
  TOTAL_SALES_AMOUNT: 'TotalSalesAmount',
  EC_TYPE: 'ECType'
};

/**
 * Validates that the file has the expected headers
 */
const validateHeaders = (headers) => {
  if (!Array.isArray(headers) || headers.length < 3) {
    return false;
  }
  
  const normalizedHeaders = headers.map(h => String(h).trim());
  return normalizedHeaders.includes(EXPECTED_HEADERS.VAT_NUMBER) &&
         normalizedHeaders.includes(EXPECTED_HEADERS.TOTAL_SALES_AMOUNT) &&
         normalizedHeaders.includes(EXPECTED_HEADERS.EC_TYPE);
};

/**
 * Validates a single row of data
 */
const validateRow = (row) => {
  const errors = [];
  
  if (!row.VATNumber || String(row.VATNumber).trim() === '') {
    errors.push('VAT Number is required');
  }
  
  if (row.TotalSalesAmount === undefined || row.TotalSalesAmount === null || row.TotalSalesAmount === '') {
    errors.push('Total Sales Amount is required');
  } else if (isNaN(parseFloat(row.TotalSalesAmount))) {
    errors.push('Total Sales Amount must be a number');
  }
  
  if (row.ECType === undefined || row.ECType === null || row.ECType === '') {
    errors.push('EC Type is required');
  } else {
    const ecTypeNum = parseInt(row.ECType, 10);
    if (isNaN(ecTypeNum) || ecTypeNum < 1 || ecTypeNum > 7) {
      errors.push('EC Type must be between 1 and 7');
    }
  }
  
  return { isValid: errors.length === 0, errors };
};

/**
 * Parses raw sheet data into EC Listing line items.
 * Returns minimal structure - same as existing grid data format.
 */
const parseSheetData = (data) => {
  const lines = [];
  const errors = [];
  
  if (!Array.isArray(data) || data.length === 0) {
    errors.push('No data found in file');
    return { lines, errors };
  }
  
  data.forEach((row, index) => {
    const rowNumber = index + 2;
    const validation = validateRow(row);
    
    if (!validation.isValid) {
      errors.push(`Row ${rowNumber}: ${validation.errors.join(', ')}`);
      return;
    }
    
    const vatNumber = String(row.VATNumber).trim();
    const amount = parseFloat(row.TotalSalesAmount);
    const ecTypeNumeric = mapECTypeFromTemplate(row.ECType);
    
    // Minimal structure matching existing grid format
    lines.push({
      VATNumberSupplier: vatNumber,
      Value: amount.toFixed(2),
      ECType: ecTypeNumeric,
      ListingType: 0,
      CorrectionType: false,
      New: true,
      coSOriginalBuyerVRN: ''
    });
  });
  
  return { lines, errors };
};

/**
 * Parses an Excel file (.xlsx/.xls) and extracts EC Listing data
 */
export const parseECListingExcel = async (file) => {
  log.debug('parseECListingExcel: Parsing file', file.name);
  
  try {
    const XLSX = await import('xlsx');
    
    return new Promise((resolve) => {
      const reader = new FileReader();
      
      reader.onload = (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const firstSheetName = workbook.SheetNames[0];
          if (!firstSheetName) {
            resolve({ success: false, lines: [], errors: ['No sheets found in the file'] });
            return;
          }
          
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          
          if (jsonData.length < 2) {
            resolve({ success: false, lines: [], errors: ['File must contain at least a header row and one data row'] });
            return;
          }
          
          const headers = jsonData[0];
          if (!validateHeaders(headers)) {
            resolve({ success: false, lines: [], errors: ['Invalid file format. Expected columns: VATNumber, TotalSalesAmount, ECType'] });
            return;
          }
          
          const dataRows = jsonData.slice(1).filter(row => row.length > 0);
          const objectData = dataRows.map(row => ({
            VATNumber: row[0],
            TotalSalesAmount: row[1],
            ECType: row[2]
          }));
          
          const { lines, errors } = parseSheetData(objectData);
          resolve({ success: errors.length === 0, lines, errors });
        } catch (parseError) {
          log.debug('parseECListingExcel: Parse error', parseError);
          resolve({ success: false, lines: [], errors: [`Failed to parse file: ${parseError.message}`] });
        }
      };
      
      reader.onerror = () => {
        resolve({ success: false, lines: [], errors: ['Failed to read file'] });
      };
      
      reader.readAsArrayBuffer(file);
    });
  } catch (importError) {
    log.debug('parseECListingExcel: Import error', importError);
    return { success: false, lines: [], errors: ['Excel parsing library not available'] };
  }
};

export default {
  parseECListingExcel,
  validateHeaders,
  validateRow,
  EXPECTED_HEADERS
};
